import {
  KeyboardEvent,
  useId,
  useRef,
  useState,
} from "react";
import { Box, ClickAwayListener, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { OverflowTooltip } from "src/domains/shared/components/OverflowTooltip";
import { LineGlyph } from "src/domains/shared/components/StateTransition";
import {
  SelectFieldState,
  SelectSize,
  selectChevronSx,
  selectEmptySx,
  selectFieldSx,
  selectInputSx,
  selectLabelSx,
  selectMenuAboveCss,
  selectMenuCss,
  selectOptionCheckSx,
  selectOptionLabelSx,
  selectOptionSx,
  selectTextSizeSx,
} from "./valueSelectStyles";

export interface ValueSelectOption<TValue> {
  value: TValue;
  label: string;
}

export interface ValueSelectProps<TValue> {
  ariaLabel: string;
  options: ValueSelectOption<TValue>[];
  value: TValue;
  onChange: (value: TValue) => void;
  /** 입력한 글자로 옵션을 거른다. 끄면 버튼형 드롭다운. */
  searchable?: boolean;
  disabled?: boolean;
  /** 선택값이 옵션에 없을 때(미선택·로딩·비활성) 보이는 문구. */
  placeholder: string;
  autoFocus?: boolean;
  /** 목록을 펼칠 방향 — 아래 공간이 모자란 자리는 위로 연다. */
  menuPlacement?: "bottom" | "top";
  /** 칸 크기 — 기본은 마이페이지 밀도(32px). */
  size?: SelectSize;
}

/**
 * 값으로 고르는 선택 콤보박스 — 검색형(입력으로 거르기)과 버튼형 드롭다운 두 가지.
 * 옵션을 값으로 받고 값으로 돌려주므로 라벨이 같은 옵션이 여럿이어도 고른 그 값이 선택된다.
 */
export const ValueSelect = <TValue,>({
  ariaLabel,
  options,
  value,
  onChange,
  searchable = false,
  disabled = false,
  placeholder,
  autoFocus = false,
  menuPlacement = "bottom",
  size = "small",
}: ValueSelectProps<TValue>) => {
  const { t } = useTranslation("projects");
  const baseId = useId();
  const listId = `${baseId}-list`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find((option) => option.value === value);
  const selectedLabel = selected?.label ?? "";
  const trimmedQuery = query.trim();
  const isFilteringByQuery = searchable && open && trimmedQuery !== "";
  const visibleOptions = isFilteringByQuery
    ? options.filter((option) => option.label.includes(trimmedQuery))
    : options;
  const optionId = (index: number) => `${baseId}-option-${index}`;

  const openMenu = () => {
    if (disabled || open) return;
    setQuery("");
    setActiveIndex(Math.max(0, options.findIndex((option) => option.value === value)));
    setOpen(true);
  };

  const closeMenu = () => {
    setOpen(false);
    setQuery("");
    setActiveIndex(-1);
  };

  const pick = (option: ValueSelectOption<TValue>) => {
    closeMenu();
    if (option.value !== value) onChange(option.value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (disabled) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        openMenu();
        return;
      }
      const step = event.key === "ArrowDown" ? 1 : -1;
      const count = visibleOptions.length;
      if (count === 0) return;
      setActiveIndex((current) => (current + step + count) % count);
      return;
    }
    if (event.key === "Enter") {
      if (!open) return;
      event.preventDefault();
      const option = visibleOptions[activeIndex];
      if (option) pick(option);
      return;
    }
    if (event.key === "Escape" && open) {
      event.preventDefault();
      event.stopPropagation();
      closeMenu();
    }
  };

  const fieldClassState: SelectFieldState = (() => {
    if (disabled) return "off";
    if (open) return "open";
    return "idle";
  })();

  const comboboxA11y = {
    role: "combobox",
    "aria-label": ariaLabel,
    "aria-expanded": open,
    "aria-controls": listId,
    "aria-haspopup": "listbox" as const,
    "aria-activedescendant": open && activeIndex >= 0 ? optionId(activeIndex) : undefined,
  };

  const field = searchable ? (
    <Box sx={selectFieldSx(fieldClassState, size)} onClick={openMenu}>
      <Box
        component="input"
        ref={inputRef}
        autoFocus={autoFocus}
        {...comboboxA11y}
        aria-autocomplete="list"
        value={open ? query : selectedLabel}
        placeholder={open ? selectedLabel || placeholder : placeholder}
        disabled={disabled}
        onFocus={openMenu}
        onChange={(event) => {
          setQuery((event.target as HTMLInputElement).value);
          setActiveIndex(0);
        }}
        onKeyDown={handleKeyDown}
        sx={{ ...selectInputSx, ...selectTextSizeSx(size) }}
      />
      <Box sx={selectChevronSx(open)}>
        <LineGlyph name="chevronDown" />
      </Box>
    </Box>
  ) : (
    <Box
      component="button"
      type="button"
      autoFocus={autoFocus}
      {...comboboxA11y}
      disabled={disabled}
      onClick={() => (open ? closeMenu() : openMenu())}
      onKeyDown={handleKeyDown}
      sx={{ ...selectFieldSx(fieldClassState, size), width: "100%", fontFamily: "inherit", textAlign: "left" }}
    >
      <Typography component="span" sx={selectLabelSx(selected === undefined, size)}>
        {selectedLabel || placeholder}
      </Typography>
      <Box sx={selectChevronSx(open)}>
        <LineGlyph name="chevronDown" />
      </Box>
    </Box>
  );

  return (
    <ClickAwayListener onClickAway={() => open && closeMenu()}>
      <Box sx={{ position: "relative", flex: 1, minWidth: 0 }}>
        <OverflowTooltip
          popupText={open ? "" : selectedLabel}
          childSelector={searchable ? "input" : "span"}
          placement="bottom-start"
        >
          {field}
        </OverflowTooltip>
        {open && (
          <Box
            component="ul"
            id={listId}
            role="listbox"
            aria-label={ariaLabel}
            css={[selectMenuCss, menuPlacement === "top" && selectMenuAboveCss]}
          >
            {visibleOptions.map((option, index) => {
              const isSelected = option.value === value;
              const isActive = index === activeIndex;
              return (
                <Box
                  component="li"
                  key={optionId(index)}
                  id={optionId(index)}
                  role="option"
                  aria-selected={isSelected}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => pick(option)}
                  sx={selectOptionSx(isSelected, isActive, size)}
                >
                  <Box component="span" sx={selectOptionLabelSx}>
                    {option.label}
                  </Box>
                  <Box component="span" sx={selectOptionCheckSx}>
                    {isSelected && <LineGlyph name="check" strokeWidth={2.2} />}
                  </Box>
                </Box>
              );
            })}
            {visibleOptions.length === 0 && (
              <Box component="li" role="presentation" sx={selectEmptySx}>
                {t("myPageModal.select.noResult", { ns: "projects" })}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};
